import { Fragment, useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { useRovingGrid } from '../a11y/useRovingGrid'
import { useHitTarget } from '../responsive/hitTarget'
import { totalsByCurrency, type CurrencyTotal } from '../money/totals'
import { Checkbox } from './Checkbox'
import { Money } from './Money'
import { SURFACE_CLASSES } from './Panel'
import './dataGrid.css'

/**
 * A transaction grid — CSS grid, never `<table>` (CLAUDE.md).
 *
 * **`role="table"` by default, `role="grid"` only with `selection`.** A grid is
 * a composite widget: the ARIA pattern requires managed focus and arrow-key
 * traversal, and screen readers switch interaction mode on it. A read-only list
 * has neither, and `table` carries the identical rows / rowgroups /
 * columnheader / rowheader model with no keyboard contract. Passing `selection`
 * makes the grid interactive, and the role changes **in the same commit** as
 * the keyboard contract (`useRovingGrid`: one tab stop, arrow keys between
 * cells, Space toggles the row, Enter activates it) — shipping the interactive
 * role first means every consumer inherits a promise nobody owns paying. It is
 * opt-in so a grid that does not select keeps its role and its DOM exactly.
 * CLAUDE.md's "never `<table>`" is about the layout mechanism, not the ARIA role.
 *
 * **The header composes `SunkWell`'s class rather than painting `--sunk`.**
 * That class carries the fill and the `--control-*` triple together (#98,
 * #116), so a control in the header resolves its ink against the well it is
 * actually painted on. web-tester verified it end-to-end: a `Button` inside the
 * header of a grid nested in a `HeroTile` resolves the well's `--control-ink`,
 * not the tile's `--on-accent`, which would be the 1.23:1 failure.
 *
 * **Do not render this in a route until #95 lands.** `assertReconciled` throws
 * during render, and with no error boundary React unwinds to the root: measured
 * as a blank document, unrecoverable without a reload. That is the correct
 * failure for a caller bug and the wrong blast radius for a production app.
 */

export type Column<T> = {
  key: string
  header: ReactNode
  /**
   * A `grid-template-columns` track.
   *
   * Prefer `minmax(0, 2fr)` over `2fr`: an `fr` track takes min-content as its
   * automatic minimum, so a long merchant name refuses to shrink and the grid
   * overflows horizontally below about 360px. Measured at 320px with
   * `2fr 1fr 1fr 110px`: the grid's own box scrolls at 351px against a 294px
   * viewport, putting the amount column off-screen.
   *
   * One invalid track invalidates the whole template — the widths are joined
   * into a single string — so `'banana'` silently collapses every column into
   * one. `assertTracks` rejects the obvious cases at the boundary.
   */
  width: string
  align?: 'start' | 'end'
  render: (row: T) => ReactNode
}

/**
 * A band of rows under a label.
 *
 * **`subtotalCents` is optional, and its absence means "this band has no money
 * in it" — not "zero".** Every group used to be required to declare one and the
 * header rendered `<Money>` unconditionally, which made this grid unusable for
 * any table that is not about amounts: M11's categories table would have shown
 * a currency-formatted zero on each of its three type bands, which is a
 * plausible wrong number of exactly the kind `assertReconciled` exists to
 * refuse. Made optional when the categories table needed it, deliberately
 * before M6's transactions table hardens around the money-only shape.
 *
 * A group that omits it renders its label alone.
 */
export type Group<T> = {
  label: string
  subtotalCents?: number
  /**
   * One subtotal per currency, for a band whose rows may span several (D21:
   * amounts in different currencies are never summed). **Mutually exclusive
   * with `subtotalCents`** — supplying both throws. Reconciled per currency, so
   * the caller must pass `rowCurrency` for it.
   */
  subtotals?: CurrencyTotal[]
  rows: T[]
}

/** Does this band carry money at all? An absent subtotal means "no money here", not zero. */
const hasSubtotal = <T,>(g: Group<T>) => g.subtotalCents !== undefined || g.subtotals !== undefined

/** Row selection. Passing it turns the grid into an interactive `role="grid"`. */
export type Selection<T> = {
  isSelected: (row: T) => boolean
  onToggle: (row: T) => void
  /** Header checkbox: `'mixed'` when some visible rows are selected. */
  allVisible: boolean | 'mixed'
  onToggleAllVisible: () => void
  /** "Select Save-On-Foods, 28 July, −$118.44" — the row's checkbox name. */
  rowLabel: (row: T) => string
}

type Props<T> = {
  columns: Column<T>[]
  groups: Group<T>[]
  rowKey: (row: T) => string
  /** Names the grid for assistive technology — a page can carry several. */
  label: string
  /**
   * Shown when no group has rows.
   *
   * Required, not optional: CLAUDE.md asks for five states on every list and
   * says empty must name the missing thing and offer one action, and a generic
   * grid cannot know the thing. Making it required moves the state from easy to
   * forget to impossible.
   *
   * Distinct from `<Async>`'s empty, which a caller may also have: Async's
   * means "no data at all", this one means "the filter matched nothing, and the
   * column headers are still shown so you can see what would be here".
   */
  empty: ReactNode
  /**
   * Opt-in reconciliation: the cents behind a row.
   *
   * `Column.render` returns a `ReactNode`, so the grid cannot pull an amount
   * out of a row by itself. Given this, each rendered group's subtotal is
   * checked against its own rows and a disagreement throws.
   *
   * **Do not pass this when `rows` is a filtered or paginated subset of what
   * `subtotalCents` covers.** A day split across a page boundary, or filtered
   * to one category while keeping the server's day total, is correct product
   * behaviour (M6 paginates) and would throw. Passing the accessor asserts
   * "these rows are the complete set this subtotal covers".
   */
  amountCents?: (row: T) => number
  /**
   * The ISO currency of a row. **Required when `amountCents` is given and any
   * group uses `subtotals`**: reconciliation is per currency and cannot guess
   * which one a row's cents are in.
   */
  rowCurrency?: (row: T) => string
  /**
   * Opt-in selection. A 40px checkbox track is prepended by the grid itself —
   * callers do not add a column for it. See the component docblock for the
   * role change and the keyboard contract that come with it.
   */
  selection?: Selection<T>
  /**
   * Opt-in: the day bands stick below the column header, which takes a fixed
   * height so they can. Off, the header and bands are exactly as they were.
   */
  stickyBands?: boolean
  /** An extra class on a data row, e.g. a "needs category" marker. */
  rowClassName?: (row: T) => string | undefined
  /** Enter on a focused row. Only meaningful with `selection`, where a row is focusable. */
  onActivate?: (row: T) => void
  /**
   * `'hidden'` keeps the column-header row in the accessibility tree but takes
   * it off screen, for a caller whose own band already names what the columns
   * hold (M7's section bands). The row then carries reset.css's `sr-only` and
   * NOT `datagrid__header`: the header rule's `position: sticky`, padding and
   * hairline would otherwise out-cascade `sr-only` (same specificity, later
   * sheet), and copying `sr-only`'s declarations here to win is the drift this
   * prop exists to avoid. Default `'visible'`.
   */
  header?: 'visible' | 'hidden'
  /**
   * Opt-in disclosure: a row that opens to reveal a detail panel.
   *
   * **Controlled, never internal state.** M11's table needs an "Expand all"
   * control that lives above the grid, and a grid holding its own open/closed
   * set could not be driven by it — the page would be asking the grid to agree
   * with a second copy of the truth. The grid renders what it is told.
   *
   * The role stays `table`. `aria-expanded` lives on the chevron BUTTON, not
   * on the row: `aria-expanded` on a row is only meaningful under `grid` or
   * `treegrid`, and both promise a keyboard contract — managed focus, arrow
   * traversal — that this component does not implement. A disclosure button is
   * the honest mapping for what this actually is.
   */
  expansion?: {
    /** False for a row with nothing to reveal — no chevron is drawn. */
    canExpand: (row: T) => boolean
    isExpanded: (row: T) => boolean
    onToggle: (row: T) => void
    /** The chevron's accessible name — "Expand Groceries", not "Expand". */
    toggleLabel: (row: T) => string
    /**
     * The open row's detail, rendered full width beneath it.
     *
     * A PANEL, not nested rows. An earlier version of this made children into
     * real rows at `aria-level={2}`, which is the right model for a tree and
     * the wrong one for this: the detail carries its own sub-grid AND a bar of
     * actions that belong to the parent, and neither fits a row constrained to
     * the parent grid's track list. A caller that genuinely wants a tree
     * should render one inside here.
     */
    renderDetail: (row: T) => ReactNode
  }
}

/**
 * A subtotal its own rows contradict is a thrown error, not a rendered number.
 *
 * The contract `Money` enforces one level down, for the same reason: a
 * plausible wrong figure is worse than a named failure, because nobody audits a
 * number that looks right. **M6-R4** — "Rows group by day with a subtotal that
 * reconciles with its rows". (An earlier version cited M6-R8, which is
 * selection across pagination — the behaviour was right and the citation was
 * not.)
 *
 * Integer arithmetic throughout: these are cents and CLAUDE.md bans float
 * arithmetic on money, so `reduce` over integers stays exact.
 *
 * The message carries the two figures because they are the entire diagnostic,
 * but not the group's label — that is a date, and #112 will wire an error
 * reporter that ingests these strings. Amounts and dates are what CLAUDE.md
 * keeps out of analytics payloads; an index identifies the group without
 * carrying either.
 */
function assertReconciled<T>(
  group: Group<T>,
  index: number,
  amountCents: (row: T) => number,
  rowCurrency: ((row: T) => string) | undefined,
): void {
  if (group.subtotals !== undefined) {
    if (group.subtotalCents !== undefined) {
      throw new Error(`DataGrid: group ${index} declares both subtotalCents and subtotals — use one.`)
    }
    if (!rowCurrency) {
      throw new Error(
        `DataGrid: group ${index} declares per-currency subtotals but no rowCurrency was supplied, ` +
          `so its rows cannot be reconciled per currency.`,
      )
    }
    // Compared as sets keyed by currency, both ways: a currency in the rows that
    // the band omits, and one the band declares that has no rows, are both
    // disagreements (a declared zero with no rows is not).
    const actual = new Map(totalsByCurrency(group.rows, amountCents, rowCurrency).map((t) => [t.currency, t.cents]))
    const declared = new Map(group.subtotals.map((t) => [t.currency, t.cents]))
    if (declared.size !== group.subtotals.length) {
      throw new Error(`DataGrid: group ${index} declares one currency more than once`)
    }
    for (const code of new Set([...actual.keys(), ...declared.keys()])) {
      const want = declared.get(code) ?? 0
      const got = actual.get(code) ?? 0
      if (want !== got) {
        throw new Error(
          `DataGrid: group ${index} declares a ${code} subtotal of ${want} but its ${code} rows sum to ${got}`,
        )
      }
    }
    return
  }

  // Passing `amountCents` asserts these rows reconcile. A group that then
  // declares no subtotal is a contradiction, and it throws rather than
  // silently skipping — a skipped check is the failure mode this whole
  // function exists to prevent, and "no subtotal" must not become the quiet
  // way to opt out of reconciliation on a grid that is about money.
  if (group.subtotalCents === undefined) {
    throw new Error(
      `DataGrid: group ${index} declares no subtotal, but amountCents was supplied — ` +
        `either give every group a subtotal or omit amountCents entirely.`,
    )
  }
  const sum = group.rows.reduce((total, row) => {
    const cents = amountCents(row)
    if (!Number.isSafeInteger(cents)) {
      throw new Error(
        `DataGrid: group ${index} has a row whose amount is not safe integer cents: ${JSON.stringify(cents)}`,
      )
    }
    return total + cents
  }, 0)
  if (sum !== group.subtotalCents) {
    throw new Error(
      `DataGrid: group ${index} declares a subtotal of ${group.subtotalCents} but its rows sum to ${sum}`,
    )
  }
}

/**
 * Reconciles every band that has rows, and returns those bands.
 *
 * Exported so the phone card list, which is not a `DataGrid`, runs the SAME
 * check the desktop grid does: two renderings of one list that each did their
 * own sums could disagree with each other. Empty bands are dropped first, so a
 * day whose rows fell off a page never blanks the app over a row nobody sees.
 */
export function reconcileGroups<T>(
  groups: readonly Group<T>[],
  amountCents: (row: T) => number,
  rowCurrency?: (row: T) => string,
): Group<T>[] {
  const shown = groups.filter((g) => g.rows.length > 0)
  shown.forEach((g, i) => assertReconciled(g, i, amountCents, rowCurrency))
  return shown
}

/**
 * A track list is one string, so one bad value destroys every column.
 *
 * `width: 'banana'` makes the whole `grid-template-columns` declaration
 * invalid: measured as a single 1226px column with all four cells stacked and
 * rows at 101px — no error, no console warning, just a silently wrong layout.
 * Cheaper to refuse at the boundary than to debug from a screenshot.
 */
const TRACK = /^(\d+(\.\d+)?(px|fr|%|em|rem|ch|vw)|auto|min-content|max-content|minmax\(.+\)|fit-content\(.+\)|calc\(.+\))$/

function assertTracks<T>(columns: Column<T>[]): void {
  if (columns.length === 0) throw new Error('DataGrid: needs at least one column')
  for (const c of columns) {
    if (!TRACK.test(c.width.trim())) {
      throw new Error(
        `DataGrid: column "${c.key}" has width ${JSON.stringify(c.width)}, which is not a grid track. ` +
          `One invalid track invalidates the whole template and collapses every column into one.`,
      )
    }
  }
}

/** A band's per-currency figures, joined by a visible middle dot. */
function Subtotals({ totals, muted }: { totals: CurrencyTotal[]; muted: boolean }) {
  return (
    <>
      {totals.map((t, i) => (
        <Fragment key={t.currency}>
          {i > 0 && <span aria-hidden="true"> · </span>}
          <span className="datagrid__subtotal" data-currency={t.currency}>
            <Money cents={t.cents} currency={t.currency} kind="flow" muted={muted} />
          </span>
        </Fragment>
      ))}
    </>
  )
}

export function DataGrid<T>({
  columns,
  groups,
  rowKey,
  label,
  empty,
  amountCents,
  rowCurrency,
  selection,
  stickyBands,
  rowClassName,
  onActivate,
  expansion,
  header = 'visible',
}: Props<T>) {
  // `'page'` — `e2e/hitTargets.mjs`'s `fineFloorFor` cannot read a floor off a
  // class it does not recognise and falls back to the strictest, so declaring
  // anything smaller would permanently disagree with the sweep above 640px.
  const target = useHitTarget('page')
  const gridRef = useRef<HTMLDivElement>(null)
  assertTracks(columns)
  const selectable = selection !== undefined
  // The checkbox track never drops under the hit-target floor, which is 44px on
  // a phone and would otherwise overflow a 40px track.
  const template = (selectable ? [`${Math.max(stickyBands ? 34 : 40, target)}px`] : [])
    .concat(columns.map((c) => c.width))
    .join(' ')
  const cellRole = selectable ? 'gridcell' : 'cell'
  /** Columns as ARIA counts them: the checkbox track is one. */
  const span = columns.length + (selectable ? 1 : 0)

  // A group carrying no rows is a subtotal with nothing under it, not a group.
  // Filtered FIRST, so reconciliation never runs on a band the grid has already
  // decided not to render — a paginated day whose rows fell off the page would
  // otherwise blank the app for a row nobody sees.
  const shown = amountCents ? reconcileGroups(groups, amountCents, rowCurrency) : groups.filter((g) => g.rows.length > 0)

  // Rows are numbered for `useRovingGrid`: the header is row 0, data rows follow
  // in order, and a band takes no number because it holds nothing focusable.
  const rowIndex = new Map<string, number>()
  shown.forEach((g) => g.rows.forEach((row) => rowIndex.set(rowKey(row), rowIndex.size + 1)))
  const roving = useRovingGrid(gridRef, {
    rows: rowIndex.size + 1,
    cols: columns.length + 1,
    enabled: selectable,
  })

  /** Space and Enter act on a row only when the CELL itself has focus. A control
   *  inside the cell (the checkbox, a link) handles its own keys, and answering
   *  them here as well would toggle a row twice. */
  function onRowKeyDown(row: T, e: KeyboardEvent<HTMLElement>) {
    if (!selection || !(e.target as HTMLElement).hasAttribute('data-cell')) return
    if (e.key === ' ') {
      e.preventDefault()
      selection.onToggle(row)
    } else if (e.key === 'Enter' && onActivate) {
      e.preventDefault()
      onActivate(row)
    }
  }

  const cellClass = (align: Column<T>['align']) =>
    align === 'end' ? 'datagrid__cell--end' : 'datagrid__cell--start'

  /**
   * One row, plus its detail panel when open.
   *
   * The detail is a sibling `role="row"` holding a single cell that spans
   * every column — not a child of the parent row, because a row may not
   * contain another row.
   */
  function renderRow(row: T): ReactNode[] {
    const expandable = expansion ? expansion.canExpand(row) : false
    const openRow = expandable && expansion!.isExpanded(row)

    const selected = selection?.isSelected(row) ?? false
    const ri = rowIndex.get(rowKey(row)) ?? 0
    const extra = rowClassName?.(row)

    const rendered: ReactNode[] = [
      <div
        key={rowKey(row)}
        className={`datagrid__row hoverable${selected ? ' datagrid__row--selected' : ''}${extra ? ` ${extra}` : ''}`}
        role="row"
        aria-selected={selection ? selected : undefined}
        onKeyDown={selection ? (e) => onRowKeyDown(row, e) : undefined}
        style={{ gridTemplateColumns: template }}
      >
        {selection && (
          <span role="gridcell" className="datagrid__cell--select" data-cell={`${ri}:0`}>
            <Checkbox
              checked={selected}
              onChange={() => selection.onToggle(row)}
              label={selection.rowLabel(row)}
            />
          </span>
        )}
        {columns.map((c, i) => (
          <span
            key={c.key}
            role={cellRole}
            data-cell={selectable ? `${ri}:${i + 1}` : undefined}
            // The lead cell lays its chevron and its content out on ONE line.
            // Without this the cell is a plain inline box, the two stack, and
            // the row renders at double height - seen in the first browser
            // screenshot of the categories table and invisible to every unit
            // test, since jsdom lays out nothing.
            className={`${cellClass(c.align)}${i === 0 && expansion ? ' datagrid__cell--lead' : ''}`}
          >
            {i === 0 && expansion && (
              // The slot is rendered for EVERY row, including one that cannot
              // expand, so each row's content starts at the same left edge.
              // The placeholder is an empty aria-hidden box rather than a
              // disabled button - a disabled control implies the reason could
              // change, and a leaf row will never grow children.
              <span
                className={`datagrid__chevron-slot${openRow ? ' datagrid__chevron-slot--open' : ''}`}
                // The slot reserves the chevron's own width whether or not a
                // chevron is in it, so every row shares one left edge. It had
                // a fixed 20px until the chevron gained a hit-target floor,
                // at which point the slot collapsed on LEAF rows and their
                // glyphs shifted left of the expandable ones — visible
                // immediately in the table, invisible to jsdom.
                style={{ minWidth: target }}
              >
                {expandable ? (
                  <button
                    type="button"
                    className="datagrid__chevron"
                    // The floor, inline, because only an inline declaration
                    // reliably outranks whatever else touches the box (#90).
                    // The browser sweep measured this at 20x20 against a 44px
                    // coarse floor — a chevron sized to its glyph rather than
                    // to a thumb.
                    style={{ minHeight: target, minWidth: target }}
                    aria-expanded={openRow}
                    aria-label={expansion.toggleLabel(row)}
                    onClick={() => expansion.onToggle(row)}
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                ) : (
                  <span aria-hidden="true" />
                )}
              </span>
            )}
            {c.render(row)}
          </span>
        ))}
      </div>,
    ]

    if (openRow) {
      rendered.push(
        <div key={`${rowKey(row)}:detail`} className="datagrid__detail" role="row">
          <span role={cellRole} aria-colspan={span}>
            {expansion!.renderDetail(row)}
          </span>
        </div>,
      )
    }
    return rendered
  }

  return (
    <div
      className={
        'datagrid' + (stickyBands ? ' datagrid--sticky-bands' : '') + (selectable ? ' datagrid--selectable' : '')
      }
      // The hit-target floor, handed to CSS so the sticky header's height is
      // computed from it rather than a literal that a 44px phone floor outgrows.
      style={stickyBands ? ({ '--datagrid-hit': `${target}px` } as CSSProperties) : undefined}
    >
      {/* The scroll container AND the sticky header's containing block. The
          header is a direct child so it has the full list to travel over —
          wrapping it in its own rowgroup confined it to a box its own height
          and it drifted with the scroll. A `table` may own rows directly, so
          the ARIA model is intact without the wrapper. */}
      <div
        ref={gridRef}
        className="datagrid__table"
        role={selectable ? 'grid' : 'table'}
        aria-label={label}
        aria-colcount={columns.length + (selectable ? 1 : 0)}
        {...(selectable ? roving : {})}
        // Counts the rows actually rendered, so an open parent's children are
        // included and the number moves as rows open and close. A count of
        // top-level rows only would tell a screen reader there are 14 rows
        // while it walks 30. The `+ 1` for the band row is skipped for the
        // same groups that skip rendering it below — an empty label with no
        // subtotal has nothing to announce, so it emits no row at all.
        aria-rowcount={shown.reduce(
          (n, g) => n + g.rows.length + (g.label === '' && !hasSubtotal(g) ? 0 : 1),
          1,
        )}
      >
        <div
          className={header === 'hidden' ? 'sr-only' : `datagrid__header ${SURFACE_CLASSES.sunkWell}`}
          role="row"
          style={{ gridTemplateColumns: template }}
          onKeyDown={
            selection
              ? (e) => {
                  if (e.key === ' ' && (e.target as HTMLElement).dataset.cell === '0:0') {
                    e.preventDefault()
                    selection.onToggleAllVisible()
                  }
                }
              : undefined
          }
        >
          {selection && (
            <span role="columnheader" className="datagrid__cell--select" data-cell="0:0">
              <Checkbox
                checked={selection.allVisible}
                onChange={() => selection.onToggleAllVisible()}
                label="Select all visible rows"
              />
            </span>
          )}
          {columns.map((c, i) => (
            <span
              key={c.key}
              role="columnheader"
              data-cell={selectable ? `0:${i + 1}` : undefined}
              className={`${cellClass(c.align)}${i === 0 && expansion ? ' datagrid__cell--lead' : ''}`}
            >
              {/* Mirrors the row's own chevron slot — width and end margin,
                  no glyph — so the label sits directly above the row's own
                  content rather than above its chevron. A leaf row still
                  reserves the identical slot (`renderRow` above, "every row
                  shares a left edge"), so the header's own copy of it lines
                  every row up under one label instead of only some of them.
                  Its own class, not `.datagrid__chevron-slot` again:
                  `DataGrid.test.tsx`'s "a leaf row reserves the chevron
                  slot" test counts that class once per ROW, and a header is
                  not one of the rows it counts. */}
              {i === 0 && expansion && (
                <span
                  className="datagrid__header-spacer"
                  aria-hidden="true"
                  style={{ minWidth: target }}
                />
              )}
              {c.header}
            </span>
          ))}
        </div>

        {shown.map((g, i) => (
          <div key={`${i}:${g.label}`} role="rowgroup">
            {/* An empty label with no subtotal has nothing to announce — the
                same false-value judgement the empty-cell branch below makes
                eight lines down, applied to the whole row instead of one
                cell. Rendering it anyway put a `role="rowheader"` with no
                text, and thus a `role="row"` with no content, into the DOM:
                announced by a screen reader and occupying layout height. */}
            {!(g.label === '' && !hasSubtotal(g)) && (
              <div className="datagrid__group" role="row">
                {/* The label spans the columns the subtotal does not. At one
                    column there is nothing to span, so both share the single
                    cell rather than claiming two against a one-column header. */}
                {g.subtotals !== undefined && g.subtotals.length > 0 ? (
                  // Per-currency band. One figure per currency, never a sum
                  // (D21), each wrapped so it is a countable element and so a
                  // currency stays attached to its own amount.
                  span > 1 ? (
                    <>
                      <span role="rowheader" aria-colspan={span - 1}>
                        {g.label}
                      </span>
                      <span role="gridcell" className="datagrid__subtotals">
                        <Subtotals totals={g.subtotals} muted={stickyBands === true} />
                      </span>
                    </>
                  ) : (
                    <span role="rowheader">
                      {g.label} <Subtotals totals={g.subtotals} muted={stickyBands === true} />
                    </span>
                  )
                ) : g.subtotalCents === undefined ? (
                  // No money in this band. The label claims every column rather
                  // than leaving an empty cell where an amount would be — an
                  // empty gridcell reads to a screen reader as a missing value,
                  // which is a different claim from "this table has no amounts".
                  <span role="rowheader" aria-colspan={span}>
                    {g.label}
                  </span>
                ) : span > 1 ? (
                  <>
                    <span role="rowheader" aria-colspan={span - 1}>
                      {g.label}
                    </span>
                    <span role="gridcell">
                      <Money cents={g.subtotalCents} kind="flow" muted={stickyBands === true} />
                    </span>
                  </>
                ) : (
                  <span role="rowheader">
                    {g.label} <Money cents={g.subtotalCents} kind="flow" muted={stickyBands === true} />
                  </span>
                )}
              </div>
            )}

            {g.rows.flatMap((row) => renderRow(row))}
          </div>
        ))}
      </div>

      {/* Outside the table element: `role="table"` owns rows and rowgroups, and
          a role-less generic between it and its content is the same structural
          defect this component corrected in the group branch. */}
      {shown.length === 0 && <div className="datagrid__empty">{empty}</div>}
    </div>
  )
}
